
import { defineComponent, onBeforeMount, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'

// Components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// Classes
import { DataState } from '@/core/classes/DataState.class'

// Types
import {
    TmcDirectionItem,
    TmcT,
    TmcCreateRelocationPayload,
    RelocationT,
} from '@/core/types/tmc.types'
import { WarehouseT } from '@/core/types/warehouses.types'

// Realisations
import { tmc } from '@/core/realisations/tmc.realisation'
import { warehouses } from '@/core/realisations/warehouses.realisation'

// Validation
import useVuelidate from '@vuelidate/core'
import { required, integer } from '@vuelidate/validators'

// Composable
import useHelpers from '@/common/composable/useHelpers'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        'the-select': TheSelect,
    },
    setup() {
        const router = useRouter()
        const route = useRoute()

        const { findItemIndexById } = useHelpers()
        const notifications = useNotifications()

        const relocation_id = Number(route.params.relocation_id as string)

        const state = reactive({
            jiraNumber: null as string | null,
            fromWarehouseId: null as number | null,
            toWarehouseId: null as number | null,
            //
            items: [] as {
                tmc: number
                tmc_name: string
                amount: number
                directionItemId?: number
            }[],
            //
            is_loading: false,
            //
            relocation: null as RelocationT | null,
            is_relocation_loading: false,
            is_relocation_failed: false,
            //
            is_uncompleted_item_added: true,
            is_item_removing: false,
        })

        const data_state = reactive({
            warehouses: new DataState<WarehouseT>({}),
            second_warehouses: new DataState<WarehouseT>({}),
            tmc: new DataState<TmcT>({}),
        })

        const item_state = reactive({
            is_update: false,
            update_item_index: null as number | null,
            tmc: null as number | null,
            amount: null as string | null,
        })

        const state_validation = useVuelidate(
            {
                jiraNumber: { required },
                fromWarehouseId: { required },
                toWarehouseId: { required },
                items: { required },
            },
            state
        )

        const item_validation = useVuelidate(
            {
                tmc: { required },
                amount: { required, integer },
            },
            item_state
        )

        // Relocation
        const initializeRelocation = () => {
            if (!state.relocation) return

            state.jiraNumber = state.relocation.jiraNumber
            state.fromWarehouseId = state.relocation.fromWarehouseId
            state.toWarehouseId = state.relocation.toWarehouseId

            state.items = state.relocation.directionItems.map((directionItem) => {
                return {
                    tmc: directionItem.inventoryItemId,
                    tmc_name: directionItem.inventoryItemName,
                    amount: directionItem.amount,
                    directionItemId: directionItem.id,
                }
            })
        }

        const loadRelocation = async () => {
            try {
                state.is_relocation_loading = true

                const response = await tmc.getRelocation(relocation_id)
                state.relocation = response.data

                initializeRelocation()

                state.is_relocation_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке перемещения произошла ошибка',
                })
                state.is_relocation_failed = true
            } finally {
                state.is_relocation_loading = false
            }
        }

        // TMC
        const loadTMC = async (page: number, search?: string) => {
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true
                const response = await tmc.getList(page, search)
                data_state.tmc.list = response.data

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const loadMoreTMC = async (page: number) => {
            if (page > data_state.tmc.pagination.total_pages) return
            data_state.tmc.pagination.current_page = page

            try {
                data_state.tmc.is_loading = true

                const response = await tmc.getList(page)
                data_state.tmc.list = data_state.tmc.list.concat(response.data)

                data_state.tmc.pagination.total_pages = response.meta.last_page

                data_state.tmc.is_failed = false
                data_state.tmc.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.tmc.failure.catchError(error)

                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_failed = true
                }
            } finally {
                if (!data_state.tmc.failure.error_state.is_cancel) {
                    data_state.tmc.is_loading = false
                }
            }
        }

        const searchTMC = (search_query: string) => {
            loadTMC(1, search_query)
        }

        const unshiftFindedTMC = async (tmc: TmcT) => {
            await loadTMC(data_state.tmc.pagination.current_page)
            data_state.tmc.list.unshift(tmc)
        }

        // Warehouses
        const loadWarehouses = async () => {
            data_state.warehouses.pagination.current_page = 1

            try {
                data_state.warehouses.is_loading = true
                const response = await warehouses.getWarehouses()
                data_state.warehouses.list = response.data

                // data_state.warehouses.pagination.total_pages = response.meta.last_page

                data_state.warehouses.is_failed = false
                data_state.warehouses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.warehouses.failure.catchError(error)

                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_failed = true
                }
            } finally {
                if (!data_state.warehouses.failure.error_state.is_cancel) {
                    data_state.warehouses.is_loading = false
                }
            }
        }
        const loadSecondWarehouses = async () => {
            data_state.second_warehouses.pagination.current_page = 1

            try {
                data_state.second_warehouses.is_loading = true
                const response = await warehouses.getWarehouses()
                data_state.second_warehouses.list = response.data

                // data_state.warehouses.pagination.total_pages = response.meta.last_page

                data_state.second_warehouses.is_failed = false
                data_state.second_warehouses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.second_warehouses.failure.catchError(error)

                if (!data_state.second_warehouses.failure.error_state.is_cancel) {
                    data_state.second_warehouses.is_failed = true
                }
            } finally {
                if (!data_state.second_warehouses.failure.error_state.is_cancel) {
                    data_state.second_warehouses.is_loading = false
                }
            }
        }

        onBeforeMount(async () => {
            await loadRelocation()
            loadTMC(1)
            await loadWarehouses()
            loadSecondWarehouses()
        })

        const resetItemForm = () => {
            item_state.tmc = null
            item_state.amount = null
            item_state.is_update = false
            item_state.update_item_index = null

            item_validation.value.$reset()
        }

        const addItem = () => {
            item_validation.value.$touch()
            if (item_validation.value.$invalid) return

            const index = findItemIndexById(item_state.tmc as number, data_state.tmc.list)
            if (index < 0) return

            const tmc_name = data_state.tmc.list[index].name

            const maybe_selected_tmc = state.items.find((item) => item.tmc === item_state.tmc)
            const maybe_selected_tmc_index = state.items.findIndex(
                (item) => item.tmc === item_state.tmc
            )

            if (
                (!item_state.is_update && maybe_selected_tmc) ||
                (item_state.is_update &&
                    maybe_selected_tmc_index !== item_state.update_item_index &&
                    maybe_selected_tmc)
            ) {
                notifications.notify({
                    title: `ТМЦ '${tmc_name}' уже выбран`,
                    description: 'Пожалуйста, выберите другой ТМЦ',
                    type: 'danger',
                })
                return
            }

            const item = {
                tmc: item_state.tmc as number,
                tmc_name,
                amount: Number(item_state.amount as string),
            }

            if (item_state.is_update) {
                state.items[item_state.update_item_index as number].tmc = item.tmc
                state.items[item_state.update_item_index as number].tmc_name = item.tmc_name
                state.items[item_state.update_item_index as number].amount = item.amount
            } else {
                state.items.push(item)
            }

            resetItemForm()
            if (!state.is_uncompleted_item_added) state.is_uncompleted_item_added = true
        }

        const removeItem = async (item_index: number) => {
            if (state.is_item_removing) return

            const answer = confirm('Вы уверены что хотите удалить товар из перемещения?')
            if (!answer) return

            try {
                state.is_item_removing = true

                const item = state.items[item_index]

                if (item.directionItemId) {
                    await tmc.deleteDirectionItem(item.directionItemId)
                }

                state.items.splice(item_index, 1)
                notifications.notify({ title: 'Товар удален из перемещения', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении товара из перемещения произошла ошибка',
                })
            } finally {
                state.is_item_removing = false
            }
        }

        const editItem = () => {
            item_validation.value.$touch()
            if (item_validation.value.$invalid) return

            const index = findItemIndexById(item_state.tmc as number, data_state.tmc.list)
            if (index < 0) return

            const tmc_name = data_state.tmc.list[index].name
            const maybe_selected_tmc = state.items.find(
                (item, index) =>
                    item.tmc === item_state.tmc && index !== item_state.update_item_index
            )

            if (maybe_selected_tmc) {
                notifications.notify({
                    title: `ТМЦ '${tmc_name}' уже выбран`,
                    description: 'Пожалуйста, выберите другой ТМЦ',
                    type: 'danger',
                })
                return
            }

            state.items[item_state.update_item_index as number].tmc = item_state.tmc as number
            state.items[item_state.update_item_index as number].tmc_name = tmc_name
            state.items[item_state.update_item_index as number].amount = Number(
                item_state.amount as string
            )
            resetItemForm()
        }

        const startEditItem = (item_index: number) => {
            item_state.tmc = state.items[item_index].tmc
            item_state.amount = state.items[item_index].amount.toString()
            item_state.update_item_index = item_index
            item_state.is_update = true
        }

        const mapItems = (item: {
            tmc: number
            tmc_name: string
            amount: number
            directionItemId?: number
        }): TmcDirectionItem => {
            return {
                directionItemId: item.directionItemId,
                inventoryItemId: item.tmc,
                amount: item.amount,
            }
        }

        const submitForm = async () => {
            if (state.is_loading) return

            if (item_state.is_update) {
                notifications.notify({
                    title: 'Пожалуйста, завершите редактирование товара',
                    type: 'warning',
                })
                return
            }

            if (
                state.items.length === 0 ||
                (state.items.length > 0 && (item_state.tmc || item_state.amount))
            ) {
                state.is_uncompleted_item_added = false
                addItem()
            }

            if (!state.is_uncompleted_item_added) {
                return
            }

            state_validation.value.$touch()

            if (state_validation.value.$invalid) {
                if (state.items.length === 0) {
                    notifications.notify({
                        title: 'Ошибка валидации',
                        description:
                            'Для редактирования заявки необходимо добавить хотя бы 1 товар',
                        type: 'danger',
                    })
                }

                return
            }

            try {
                state.is_loading = true

                const payload: TmcCreateRelocationPayload = {
                    jiraNumber: state.jiraNumber as string,
                    fromWarehouseId: state.fromWarehouseId as number,
                    toWarehouseId: state.toWarehouseId as number,
                    directionItems: state.items.map(mapItems),
                }

                await tmc.updateRelocation(relocation_id, payload)

                notifications.notify({ title: 'Заявка на перемещение сохранена', type: 'success' })
                router.push('/tmc/relocations')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    title: 'При сохранении перемещения произошла ошибка',
                    error,
                })
            } finally {
                state.is_loading = false
            }
        }

        return {
            state,
            data_state,
            item_state,
            //
            state_validation,
            item_validation,
            //
            loadTMC,
            loadMoreTMC,
            searchTMC,
            unshiftFindedTMC,
            loadWarehouses,
            loadSecondWarehouses,
            //
            addItem,
            removeItem,
            startEditItem,
            editItem,
            submitForm,
        }
    },
})
